import { z } from "zod";

/**
 * Environment configuration with runtime validation.
 *
 * Validates all required environment variables on app startup.
 * If validation fails, the app will not start and will display
 * a clear error message indicating which variable is invalid.
 *
 * @remarks
 * - `DEV` is automatically provided by Vite (true in development mode)
 * - Only `VITE_*` prefixed variables are exposed to the client bundle
 * - Sentry is optional and can be omitted for development
 *
 * @see {@link https://vitejs.dev/guide/env-and-mode.html | Vite Env Variables}
 */
const envSchema = z.object({
  DEV: z.boolean(),
  VITE_SUPABASE_URL: z
    .string()
    .url()
    .refine((url) => url.includes("supabase.co"), {
      message: "Must be a valid Supabase URL",
    }),
  VITE_SUPABASE_PUBLISHABLE_KEY: z
    .string()
    .min(20, "Supabase key is too short")
    .regex(/^(eyJ|sb_publishable_)/, {
      message:
        "Invalid Supabase key format (must start with 'eyJ' or 'sb_publishable_')",
    }),
  VITE_SENTRY_DSN: z
    .string()
    .url()
    .regex(/sentry\.io/, "Must be a valid Sentry DSN")
    .optional(),
  VITE_STRIPE_PUBLISHABLE_KEY: z.string().startsWith("pk_"),
  VITE_N8N_URL: z.string().url().refine(
    (url) => url.includes("app.n8n.cloud"),
    {
      message: "Must be a valid n8n cloud URL",
    },
  ),
});

export const env = envSchema.parse(import.meta.env);

export const __DEV__ = env.DEV;
export const SENTRY_DSN = env.VITE_SENTRY_DSN;
export const SUPABASE_PUBLISHABLE_KEY = env.VITE_SUPABASE_PUBLISHABLE_KEY;
export const SUPABASE_URL = env.VITE_SUPABASE_URL;
export const STRIPE_PUBLISHABLE_KEY = env.VITE_STRIPE_PUBLISHABLE_KEY;
export const N8N_URL = env.VITE_N8N_URL;

export type Env = z.infer<typeof envSchema>;
